<!--
  Admin-only "FULL LOG" viewer. Opened from the ServerArea navbar widget, it
  streams the app-wide audit trail (agents AND everyone else) from
  GET /api/auditlog with free-text search and entity / agent filters. Unlike
  <AuditLogTable> (scoped to one entity+id), this shows the whole trail so an
  admin can audit precisely who did what across the app.
-->
<template>
  <vs-popup
    class="full-audit-log-popup"
    title="Full audit log"
    :active.sync="isActive"
  >
    <!-- Toolbar -->
    <div class="fal-toolbar">
      <vs-input
        v-model="search"
        icon="search"
        icon-after
        placeholder="Search who / message / entity / quotation…"
        class="fal-search"
        @keyup.enter.native="fetchLogs"
      />

      <select v-model="entityFilter" class="fal-select" @change="fetchLogs">
        <option value="">All entities</option>
        <option v-for="e in entities" :key="e" :value="e">{{ e }}</option>
      </select>

      <select v-model.number="segments" class="fal-select" @change="fetchLogs" title="History depth">
        <option :value="2">Recent</option>
        <option :value="4">Wide</option>
        <option :value="10">Full history</option>
      </select>

      <label class="fal-check">
        <input type="checkbox" v-model="agentsOnly" @change="fetchLogs" />
        <span>Agents only</span>
      </label>

      <vs-button size="small" type="border" icon="refresh" @click="fetchLogs">
        Refresh
      </vs-button>
    </div>

    <!-- Status line -->
    <div class="fal-status">
      <span v-if="loading">Loading…</span>
      <span v-else-if="error" class="fal-error">{{ error }}</span>
      <span v-else>
        {{ entries.length }} entr{{ entries.length === 1 ? 'y' : 'ies' }}
        <span v-if="entries.length >= limit" class="fal-muted"> (capped at {{ limit }} — narrow the search)</span>
      </span>
    </div>

    <!-- Table -->
    <div class="fal-table-wrap">
      <table class="fal-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Who</th>
            <th>Entity</th>
            <th>Message</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!loading && entries.length === 0">
            <td colspan="5" class="fal-empty">No matching log entries.</td>
          </tr>
          <tr v-for="(log, index) in entries" :key="index">
            <td class="fal-nowrap">{{ log.date }}</td>
            <td class="fal-nowrap">
              {{ log.who || '—' }}
              <span v-if="log.agent" class="fal-agent-pill">🤖 {{ log.agent }}</span>
            </td>
            <td class="fal-nowrap">
              <span v-if="log.entity">{{ log.entity }}</span>
              <span v-if="entityRef(log)" class="fal-muted">#{{ entityRef(log) }}</span>
              <span v-if="!log.entity && !entityRef(log)">—</span>
            </td>
            <td>{{ log.message }}</td>
            <td class="fal-details">
              <!-- Field-level modifications -->
              <template v-if="log.modifications && log.modifications.length">
                <div v-for="(mod, i) in log.modifications" :key="'m' + i" class="fal-mod">
                  <strong>{{ mod.champ }}:</strong>
                  <span class="fal-before" v-html="mod.avant"></span>
                  <span class="fal-arrow">&rarr;</span>
                  <span class="fal-after" v-html="mod.apres"></span>
                </div>
              </template>
              <!-- Every other structured field, generically -->
              <template v-for="key in detailKeys(log)">
                <div :key="key" class="fal-kv">
                  <strong>{{ key }}:</strong> <span>{{ formatVal(log[key]) }}</span>
                </div>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </vs-popup>
</template>

<script>
import axios from "@/axios.js";

// Keys that are rendered in their own column or handled specially — everything
// else on an entry is surfaced verbatim in the Details column.
const META_KEYS = new Set([
  "date", "who", "message", "entity", "entityId",
  "idoperation", "idperson", "level", "modifications",
]);

export default {
  name: "FullAuditLog",
  props: {
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      entries: [],
      loading: false,
      error: "",
      search: "",
      entityFilter: "",
      agentsOnly: false,
      segments: 4,
      limit: 300,
      entities: [
        "operation", "person", "user", "product", "payment",
        "valuation", "budget", "document", "employee", "agent",
      ],
    };
  },
  computed: {
    // Proxy so the parent can use :active.sync on this component.
    isActive: {
      get() { return this.active; },
      set(val) { this.$emit("update:active", val); },
    },
  },
  watch: {
    active(val) {
      if (val) this.fetchLogs();
    },
  },
  methods: {
    async fetchLogs() {
      this.loading = true;
      this.error = "";
      try {
        const params = { limit: this.limit, segments: this.segments };
        if (this.search) params.q = this.search;
        if (this.entityFilter) params.entity = this.entityFilter;
        if (this.agentsOnly) params.agentsOnly = true;
        const res = await axios.get("/auditlog", { params });
        this.entries = (res.data && res.data.entries) || [];
      } catch (e) {
        this.entries = [];
        this.error =
          (e.response && e.response.data && e.response.data.error) ||
          e.message ||
          "Failed to load logs.";
      } finally {
        this.loading = false;
      }
    },
    // Back-compat: older lines carry idoperation/idperson instead of entityId.
    entityRef(log) {
      return log.entityId != null
        ? log.entityId
        : (log.idoperation != null ? log.idoperation : (log.idperson != null ? log.idperson : null));
    },
    detailKeys(log) {
      return Object.keys(log).filter(
        (k) => !META_KEYS.has(k) && log[k] !== null && log[k] !== undefined && log[k] !== ""
      );
    },
    formatVal(v) {
      if (Array.isArray(v)) {
        return v
          .map((x) => (x && typeof x === "object" ? (x.name || x.description || JSON.stringify(x)) : x))
          .join(", ");
      }
      if (v && typeof v === "object") return JSON.stringify(v);
      return String(v);
    },
  },
};
</script>

<style>
/* vs-popup renders to body, so target it un-scoped to widen the dialog. */
.full-audit-log-popup .vs-popup {
  width: 92vw;
  max-width: 1200px;
}
.full-audit-log-popup .vs-popup--content {
  max-height: 78vh;
}
</style>

<style scoped>
.fal-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.fal-search { flex: 1 1 280px; }
.fal-select {
  height: 38px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #4b5563;
}
.fal-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #4b5563;
  cursor: pointer;
}
.fal-status {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 8px;
}
.fal-error { color: #ef4444; }
.fal-muted { color: #9ca3af; margin-left: 4px; }

.fal-table-wrap {
  overflow: auto;
  max-height: 60vh;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}
.fal-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}
.fal-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f9fafb;
  padding: 0.6rem 0.75rem;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  border-bottom: 1px solid #d1d5db;
}
.fal-table tbody td {
  padding: 0.55rem 0.75rem;
  border-bottom: 1px solid #eef0f2;
  vertical-align: top;
}
.fal-table tbody tr:hover { background: #f9fafb; }
.fal-nowrap { white-space: nowrap; }
.fal-empty { text-align: center; color: #9ca3af; padding: 1.5rem; }

.fal-agent-pill {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 10px;
  background: #ede9fe;
  color: #6d28d9;
  font-size: 0.7rem;
  white-space: nowrap;
}
.fal-details { max-width: 420px; }
.fal-mod, .fal-kv { margin-bottom: 3px; word-break: break-word; }
.fal-before { color: #ef4444; text-decoration: line-through; margin-left: 4px; }
.fal-after { color: #16a34a; }
.fal-arrow { margin: 0 4px; }
.fal-kv strong { color: #374151; }
</style>
